import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import moment from 'moment';
import {useSelector} from 'react-redux';
import DatePicker from '../feature/DatePicker';
import {
  getCustomRange,
  setCustomRange
} from '../../helpers/localStorage/CustomRangeStorage';
import {
  getStoragePeriod,
  setStoragePeriod
} from '../../helpers/localStorage/DatePeriodStorage';
import {setStorageDate} from '../../helpers/localStorage/DateStorage';
import {ButtonPrimary} from '../buttons';
import {
  getAnalyticsTodayRange,
  getAnalyticsYesterdayRange,
  getLocationWallClockNow
} from '../../helpers/functionUtils/analyticsBusinessDayRange';
import {ANALYTICS_DATEPICKER_INDEX} from '../../helpers/constants/analytics';

const PERIOD_LABELS = [
  'All period',
  'Today',
  'This week',
  'Last week',
  'This month',
  'Last month',
  'Custom range',
  'Yesterday'
];

const DatepickerModule = ({
  date,
  updateDate,
  updateSelectedDateIndex,
  localStorageObject: {
    storageDateName,
    storageCustomRangeName,
    storageDatePeriodName
  },
  defaultSelectIndex,
  updatedAllPeriods,
  showYesterday
}) => {
  const [selectedDate, setSelectedDate] = useState([]);
  const [open, setOpen] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(defaultSelectIndex);
  const [dateValue, setDateValue] = useState([null, null]);

  const {Timezone, CreationDate, createdAt} =
    useSelector((state) => state.user.activeLocation) || {};

  useEffect(() => {
    setDateValue(getCustomRange(storageCustomRangeName) || [null, null]);
  }, []);

  const handleOpen = () => {
    setOpen(!open);
  };

  useEffect(() => {
    const index = getStoragePeriod(storageDatePeriodName);
    if (index !== undefined && index !== null) {
      setSelectedIndex(index);
    } else {
      setSelectedIndex(defaultSelectIndex);
    }
  }, []);

  const handleListItemClick = (event, index) => {
    if (index !== ANALYTICS_DATEPICKER_INDEX.CUSTOM_RANGE) {
      setOpen(false);
    }
    setSelectedIndex(index);
    updateSelectedDateIndex(index);
    if (storageDatePeriodName) {
      setStoragePeriod(storageDatePeriodName, index);
    }
  };

  const setDate = () => {
    if (
      selectedDate.length &&
      selectedDate[0] !== null &&
      selectedDate[1] !== null
    ) {
      updateDate(selectedDate, selectedIndex);
      if (storageDateName) {
        setStorageDate(storageDateName, selectedDate);
      }
    }
  };

  const updateDatePeriod = (startPeriod, endPeriod) => {
    setSelectedDate([]);
    const periodCurrentDate = [startPeriod, endPeriod];
    updateDate(periodCurrentDate, selectedIndex);
    if (storageDateName && storageCustomRangeName) {
      setStorageDate(storageDateName, periodCurrentDate);
      setCustomRange(storageCustomRangeName, []);
    }
  };

  const datepickerOnChange = (datePickerDate) => {
    setDateValue(datePickerDate);
    if (storageCustomRangeName) {
      setCustomRange(storageCustomRangeName, datePickerDate);
    }
  };

  const locationDate = () => getLocationWallClockNow(Timezone);

  const toLocationDate = (alterDate) => {
    return moment(alterDate)
      .utcOffset(Timezone, false)
      .utc()
      .format('ddd MMM DD yyyy');
  };

  const getFromDateForAllPeriods = () => {
    let result;
    if (
      (CreationDate && !createdAt) ||
      moment(CreationDate).isSameOrBefore(createdAt)
    ) {
      result = CreationDate;
    } else if (
      (createdAt && !CreationDate) ||
      moment(createdAt).isSameOrBefore(CreationDate)
    ) {
      result = createdAt;
    } else {
      result = locationDate().subtract(40, 'year').startOf('day').format();
    }
    return result;
  };

  const formatSingleOrRangeLabel = () => {
    const startLabel = toLocationDate(date[0]);
    const endLabel = toLocationDate(date[1]);
    return startLabel === endLabel ? startLabel : `${startLabel} - ${endLabel}`;
  };

  const renderValueForIndex = () => {
    if (selectedIndex === ANALYTICS_DATEPICKER_INDEX.ALL_PERIOD) {
      return null;
    }
    if (
      selectedIndex === ANALYTICS_DATEPICKER_INDEX.TODAY ||
      selectedIndex === ANALYTICS_DATEPICKER_INDEX.YESTERDAY
    ) {
      return formatSingleOrRangeLabel();
    }
    if (selectedIndex === ANALYTICS_DATEPICKER_INDEX.LAST_WEEK) {
      return `${
        moment.parseZone(date[0]).utcOffset()
          ? moment(date[0]).utcOffset(0, false).format('ddd MMM DD yyyy')
          : toLocationDate(date[0])
      } - ${
        moment.parseZone(date[1]).utcOffset()
          ? moment(date[1]).utcOffset(0, true).format('ddd MMM DD yyyy')
          : toLocationDate(date[1])
      }`;
    }
    return `${toLocationDate(date[0])} - ${toLocationDate(date[1])}`;
  };

  return (
    <div className='Say2EatDrop DropPeriod'>
      <FormControl variant='outlined'>
        <InputLabel
          id='demo-simple-select-outlined-label'
          className='customRangeLabel'
        >
          {PERIOD_LABELS[selectedIndex]}
        </InputLabel>
        <Select
          labelId='demo-simple-select-outlined-label'
          id='demo-simple-select-outlined'
          multiple
          MenuProps={{
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left'
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left'
            },
            getContentAnchorEl: null
          }}
          value={date}
          renderValue={renderValueForIndex}
          open={open}
          onClose={handleOpen}
          onOpen={handleOpen}
          onChange={() => {}}
        >
          <div className='DropDown DropFilterDate'>
            <div className='DropDownHolder'>
              <div className='DropDownList'>
                <List
                  component='nav'
                  className='DropDownMenu'
                  aria-label='main mailbox folders'
                >
                  <ListItem
                    button
                    selected={
                      selectedIndex === ANALYTICS_DATEPICKER_INDEX.ALL_PERIOD
                    }
                    onClick={(event) => {
                      updateDatePeriod(
                        updatedAllPeriods ? null : getFromDateForAllPeriods(),
                        updatedAllPeriods
                          ? null
                          : locationDate().endOf('isoWeek').utc(true).format()
                      );
                      setShowDatePicker(false);
                      handleListItemClick(
                        event,
                        ANALYTICS_DATEPICKER_INDEX.ALL_PERIOD
                      );
                    }}
                  >
                    <ListItemText primary='All period' />
                  </ListItem>
                  <ListItem
                    button
                    selected={
                      selectedIndex === ANALYTICS_DATEPICKER_INDEX.TODAY
                    }
                    onClick={(event) => {
                      setShowDatePicker(false);
                      handleListItemClick(
                        event,
                        ANALYTICS_DATEPICKER_INDEX.TODAY
                      );
                      updateDatePeriod(...getAnalyticsTodayRange({Timezone}));
                    }}
                  >
                    <ListItemText primary='Today' />
                  </ListItem>
                  {showYesterday && (
                    <ListItem
                      button
                      selected={
                        selectedIndex === ANALYTICS_DATEPICKER_INDEX.YESTERDAY
                      }
                      onClick={(event) => {
                        setShowDatePicker(false);
                        handleListItemClick(
                          event,
                          ANALYTICS_DATEPICKER_INDEX.YESTERDAY
                        );
                        updateDatePeriod(
                          ...getAnalyticsYesterdayRange({Timezone})
                        );
                      }}
                    >
                      <ListItemText primary='Yesterday' />
                    </ListItem>
                  )}
                  <ListItem
                    button
                    selected={
                      selectedIndex === ANALYTICS_DATEPICKER_INDEX.THIS_WEEK
                    }
                    onClick={(event) => {
                      setShowDatePicker(false);
                      handleListItemClick(
                        event,
                        ANALYTICS_DATEPICKER_INDEX.THIS_WEEK
                      );
                      updateDatePeriod(
                        locationDate().utc().startOf('isoWeek').format(),
                        locationDate().utc().endOf('isoWeek').format()
                      );
                    }}
                  >
                    <ListItemText primary='This week' />
                  </ListItem>
                  <ListItem
                    button
                    selected={
                      selectedIndex === ANALYTICS_DATEPICKER_INDEX.LAST_WEEK
                    }
                    onClick={(event) => {
                      setShowDatePicker(false);
                      handleListItemClick(
                        event,
                        ANALYTICS_DATEPICKER_INDEX.LAST_WEEK
                      );
                      updateDatePeriod(
                        locationDate()
                          .utc()
                          .subtract(1, 'week')
                          .startOf('isoWeek')
                          .format(),
                        locationDate()
                          .utc()
                          .subtract(1, 'week')
                          .endOf('isoWeek')
                          .format()
                      );
                    }}
                  >
                    <ListItemText primary='Last week' />
                  </ListItem>
                  <ListItem
                    button
                    selected={
                      selectedIndex === ANALYTICS_DATEPICKER_INDEX.THIS_MONTH
                    }
                    onClick={(event) => {
                      setShowDatePicker(false);
                      handleListItemClick(
                        event,
                        ANALYTICS_DATEPICKER_INDEX.THIS_MONTH
                      );
                      updateDatePeriod(
                        locationDate().utc().startOf('month').format(),
                        locationDate().utc().endOf('month').format()
                      );
                    }}
                  >
                    <ListItemText primary='This month' />
                  </ListItem>
                  <ListItem
                    button
                    selected={
                      selectedIndex === ANALYTICS_DATEPICKER_INDEX.LAST_MONTH
                    }
                    onClick={(event) => {
                      setShowDatePicker(false);
                      handleListItemClick(
                        event,
                        ANALYTICS_DATEPICKER_INDEX.LAST_MONTH
                      );
                      updateDatePeriod(
                        locationDate()
                          .utc()
                          .subtract(1, 'month')
                          .startOf('month')
                          .format(),
                        locationDate()
                          .utc()
                          .subtract(1, 'month')
                          .endOf('month')
                          .format()
                      );
                    }}
                  >
                    <ListItemText primary='Last month' />
                  </ListItem>
                  <ListItem
                    button
                    selected={
                      selectedIndex === ANALYTICS_DATEPICKER_INDEX.CUSTOM_RANGE
                    }
                    onClick={(event) => {
                      handleListItemClick(
                        event,
                        ANALYTICS_DATEPICKER_INDEX.CUSTOM_RANGE
                      );
                      setOpen(true);
                      setShowDatePicker(!showDatePicker);
                    }}
                  >
                    <ListItemText primary='Custom Range' />
                  </ListItem>
                </List>
              </div>
              {showDatePicker && (
                <div className='DropDownDatepicker'>
                  <DatePicker
                    setSelectedDate={setSelectedDate}
                    dateValue={dateValue}
                    datepickerOnChange={datepickerOnChange}
                  />
                  <div className='DropDownActions'>
                    <ButtonPrimary
                      className='ButtonSmall'
                      onClick={() => {
                        setDate();
                        handleOpen();
                      }}
                    >
                      <span>Apply</span>
                    </ButtonPrimary>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Select>
      </FormControl>
    </div>
  );
};

DatepickerModule.propTypes = {
  date: PropTypes.instanceOf(Array).isRequired,
  updateDate: PropTypes.func.isRequired,
  updateSelectedDateIndex: PropTypes.func,
  localStorageObject: PropTypes.shape({
    storageDateName: PropTypes.string,
    storageCustomRangeName: PropTypes.string,
    storageDatePeriodName: PropTypes.string
  }),
  defaultSelectIndex: PropTypes.number,
  updatedAllPeriods: PropTypes.bool,
  showYesterday: PropTypes.bool
};

DatepickerModule.defaultProps = {
  updateSelectedDateIndex: () => {},
  localStorageObject: {
    storageDateName: '',
    storageCustomRangeName: '',
    storageDatePeriodName: ''
  },
  defaultSelectIndex: 2,
  updatedAllPeriods: false,
  showYesterday: false
};

export default DatepickerModule;
